import { FC, useCallback, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useT } from '_metronic/i18n/Metronici18n'
import { ModalSize } from '../../../../../../../_metronic/helpers/enumsUI'
import { QUERIES } from '../../../../../../../_metronic/helpers/crud-helper/consts'
import { Button } from '../../../../../../components/Button/Button'
import { InfoAlert } from '../../../../../../components/Info/InfoAlert'
import { LoadingIndicator } from '../../../../../../components/Loading/LoadingIndicator'
import { useModal } from '../../../../../../components/Modal/ModalContext'
import { TriagemRiscoBadge } from '../../../../../../components/TriagemRisco/TriagemRiscoBadge'
import { TriagemTag } from '../../../../../../components/TriagemRisco/TriagemTag'
import { TriagemEvento, TriagemHistorico } from '../../../../triagem/core/_models'
import { getTriagemHistorico } from '../../../../triagem/core/_requests'
import { AlterarClassificacaoTriagemModal } from '../../modal/AlterarClassificacaoTriagemModal'
import { ConfirmarClassificacaoTriagemModal } from '../../modal/ConfirmarClassificacaoTriagemModal'
import { useTriagemEventos } from '../../hooks/useTriagemEventos'
import { TriagemDetalhe } from './TriagemDetalhe'
import { TriagemHistoricoItem } from './TriagemHistoricoItem'
import { avisoNovaTriagem } from './triagemTextos'

type TriagemCardProps = {
  idAtendimento: number
  idEmpresa?: number
  isMedico?: boolean
}

export const CHAVE_TRIAGEM_HISTORICO = 'triagem-historico'

/**
 * Card de triagem do teleatendimento: o que o motor classificou, por quê, e a decisão do médico.
 *
 * A classificação vigente aparece <b>inteira, sem clique</b>: cores, regras disparadas, sinais
 * usados, sinais não avaliados, variáveis, origem, protocolo e data. É o que o edital cobra na
 * demonstração — supervisão médica visível exige que a justificativa da cor esteja à vista junto
 * do botão de override, e não atrás de um "ver mais". Só o histórico das triagens anteriores (AC8)
 * fica colapsado, para não empurrar o restante do trilho para baixo.
 */
export const TriagemCard: FC<TriagemCardProps> = ({ idAtendimento, idEmpresa, isMedico }) => {
  const { showModal } = useModal()
  const { t } = useT()
  const queryClient = useQueryClient()
  const chave = useMemo(() => [CHAVE_TRIAGEM_HISTORICO, idAtendimento], [idAtendimento])

  const [expandido, setExpandido] = useState(false)
  const [abertas, setAbertas] = useState<number[]>([])
  const [aviso, setAviso] = useState<string | undefined>()
  const [idTriagemNaoVista, setIdTriagemNaoVista] = useState<number | undefined>()

  const { data, isLoading, isError, isFetching, refetch } = useQuery(
    chave,
    () => getTriagemHistorico(idAtendimento),
    {
      refetchOnWindowFocus: false,
    }
  )

  const vigente = data?.triagens?.find((triagem) => triagem.vigente)

  const aoDecidir = useCallback(
    (historico: TriagemHistorico) => {
      queryClient.setQueryData(chave, historico)
      queryClient.invalidateQueries(QUERIES.ATENDIMENTO_MEDICO_LIST)
    },
    [chave, queryClient]
  )

  const aoReceberEvento = useCallback(
    (evento: TriagemEvento) => {
      if (evento.idAtendimento !== idAtendimento) {
        return
      }
      refetch()
      if (evento.tipo === 'TRIAGEM_CRIADA') {
        setAviso(avisoNovaTriagem(t, evento.origem))
        setIdTriagemNaoVista(evento.idTriagem)
        setExpandido(true)
      }
    },
    [idAtendimento, refetch, t]
  )

  useTriagemEventos(idEmpresa, aoReceberEvento)

  const alternarTriagem = (id: number) => {
    setAbertas((atuais) =>
      atuais.includes(id) ? atuais.filter((aberta) => aberta !== id) : [...atuais, id]
    )
    if (id === idTriagemNaoVista) {
      setIdTriagemNaoVista(undefined)
      setAviso(undefined)
    }
  }

  const abrirAlteracao = () =>
    vigente &&
    showModal({
      title: t('triagem.alterar'),
      size: ModalSize.Medium,
      content: (
        <AlterarClassificacaoTriagemModal
          idTriagem={vigente.id}
          corEfetiva={vigente.corEfetiva}
          onDecisao={aoDecidir}
        />
      ),
    })

  const abrirConfirmacao = () =>
    vigente &&
    showModal({
      title: t('triagem.confirmar'),
      size: ModalSize.Medium,
      content: (
        <ConfirmarClassificacaoTriagemModal
          idTriagem={vigente.id}
          corEfetiva={vigente.corEfetiva}
          onDecisao={aoDecidir}
        />
      ),
    })

  if (isLoading) {
    return (
      <section className='card card-bordered shadow-sm' aria-label={t('triagem.titulo')}>
        <div className='card-body p-3'>
          <LoadingIndicator />
        </div>
      </section>
    )
  }

  // Só sem dados: se um refetch (evento de triagem) falhar com o histórico já carregado, o
  // react-query mantém o `data` anterior e o card continua mostrando a última versão válida.
  if (isError && !data) {
    return (
      <section className='card card-bordered shadow-sm' aria-label={t('triagem.titulo')}>
        <div className='card-body p-3'>
          <div className='fs-7 fw-bolder text-gray-900 mb-2'>{t('triagem.titulo')}</div>
          <InfoAlert variant='danger' size='sm' role='alert'>
            {t('triagem.erro_carregar')}
          </InfoAlert>
          <Button className='btn-light btn-sm' onClick={() => refetch()} disabled={isFetching}>
            {t('triagem.tentar_novamente')}
          </Button>
        </div>
      </section>
    )
  }

  if (!vigente) {
    return (
      <section className='card card-bordered shadow-sm' aria-label={t('triagem.titulo')}>
        <div className='card-body p-3'>
          <div className='fs-7 fw-bolder text-gray-900 mb-2'>{t('triagem.titulo')}</div>
          <p className='mb-0 text-muted'>{t('triagem.sem_triagem')}</p>
        </div>
      </section>
    )
  }

  return (
    <section className='card card-bordered shadow-sm' aria-label={t('triagem.titulo')}>
      <div className='card-body p-3'>
        <div className='fs-7 fw-bolder text-gray-900 mb-2'>{t('triagem.titulo')}</div>

        {vigente.protocolo?.emValidacao && (
          <InfoAlert variant='warning' size='sm'>
            {t('triagem.protocolo_em_validacao_aviso')}
          </InfoAlert>
        )}

        {aviso && (
          <InfoAlert variant='info' size='sm' role='status'>
            {aviso}
          </InfoAlert>
        )}

        <div className='d-flex flex-wrap align-items-center gap-2 mb-2'>
          <TriagemRiscoBadge cor={data?.corFila} titulo={t('triagem.rotulo.risco')} />
          <span className='text-muted'>{t('triagem.cor_fila')}</span>
          <TriagemRiscoBadge cor={vigente.corMotor} titulo={t('triagem.rotulo.cor_motor')} />
          <span className='text-muted'>{t('triagem.cor_motor')}</span>
          <TriagemRiscoBadge cor={vigente.corEfetiva} titulo={t('triagem.rotulo.cor_efetiva')} />
          <span className='text-muted'>{t('triagem.cor_efetiva')}</span>
        </div>

        {data?.corMantida && (
          <p className='fs-8 text-gray-900 mb-2'>
            <TriagemTag texto={t('triagem.aguardando_revisao')} />
            <span className='d-block mt-1'>
              {t('triagem.cor_mantida_explicacao', { sequencia: vigente.sequencia })}
            </span>
          </p>
        )}

        {isMedico && (
          <div className='d-flex flex-wrap gap-2 mb-2'>
            <Button className='btn-primary btn-sm' onClick={abrirConfirmacao}>
              {t('triagem.confirmar')}
            </Button>
            <Button className='btn-secondary btn-sm' onClick={abrirAlteracao}>
              {t('triagem.alterar')}
            </Button>
          </div>
        )}

        <TriagemDetalhe triagem={vigente} />

        <Button
          className='btn-link p-0 d-inline-flex'
          ariaExpanded={expandido}
          onClick={() => setExpandido((atual) => !atual)}
        >
          {expandido ? t('triagem.ocultar_historico') : t('triagem.ver_historico')}
        </Button>

        {expandido && (
          <div className='mt-3'>
            <h6 className='fs-8 fw-bolder text-gray-900 mb-1'>
              {t('triagem.historico')} ({data?.quantidadeTriagens ?? 0})
            </h6>
            <ul className='list-unstyled mb-0'>
              {data?.triagens?.map((triagem) => (
                <TriagemHistoricoItem
                  key={triagem.id}
                  triagem={triagem}
                  aberta={abertas.includes(triagem.id)}
                  naoVista={triagem.id === idTriagemNaoVista}
                  onAlternar={() => alternarTriagem(triagem.id)}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  )
}
